// Reservation.jsx — form opens as modal popup on date click

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import {
  FiPhone, FiX, FiChevronLeft, FiChevronRight,
  FiUser, FiMail, FiSmartphone, FiSend,
  FiCheckCircle, FiAlertCircle,
} from 'react-icons/fi';

const EMAILJS_SERVICE_ID  = 'service_s7ueobd';
const EMAILJS_TEMPLATE_ID = 'template_lnspw2k';
const EMAILJS_PUBLIC_KEY  = 'ho5OEe3HkHDJ5TW-4';

const PHONE = '0537-26-9489';
const TEL   = 'tel:0537269489';

const MONTHS_JA = ['1月','2月','3月','4月','5月','6月','7月','8月','9月','10月','11月','12月'];
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_JA   = ['日','月','火','水','木','金','土'];
const DAYS_EN   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const ACTIVITIES_JA = ['バギー乗車体験','チェンソーアート','ナン焼き体験','野菜収穫体験','トランポリン','ネパールレストラン','複数体験（要相談）'];
const ACTIVITIES_EN = ['Buggy Riding','Chainsaw Art','Naan Baking','Veggie Harvest','Trampoline','Nepal Restaurant','Multiple Activities (TBD)'];
const ACT_ICONS     = ['🏎️','🪚','🫓','🥦','🦘','🍛','✨'];

const TIME_SLOTS = ['9:30','10:00','10:30','11:00','11:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00'];

/* ── CALENDAR ───────────────────────────────────────────── */
function MiniCalendar({ isJa, selected, onSelect }) {
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [[year, month], setYM] = useState([today.getFullYear(), today.getMonth()]);

  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dayNames    = isJa ? DAYS_JA : DAYS_EN;
  const monthLabel  = isJa ? `${year}年 ${MONTHS_JA[month]}` : `${MONTHS_EN[month]} ${year}`;

  const prev = () => setYM(([y,m]) => m === 0 ? [y-1,11] : [y,m-1]);
  const next = () => setYM(([y,m]) => m === 11 ? [y+1,0] : [y,m+1]);

  return (
    <div className="border-[1.5px] border-[#dfd1bc] rounded-[18px] overflow-hidden">
      <div className="flex items-center justify-between bg-[#f4f9ee] px-4 py-3 border-b border-[#dfd1bc]">
        <button onClick={prev} className="w-9 h-9 rounded-full bg-[#1f4d2b]/10 hover:bg-[#1f4d2b]/20 flex items-center justify-center text-[#1f4d2b] transition">
          <FiChevronLeft size={16} />
        </button>
        <span className="text-xl font-bold text-[#1a3d20]">{monthLabel}</span>
        <button onClick={next} className="w-9 h-9 rounded-full bg-[#1f4d2b]/10 hover:bg-[#1f4d2b]/20 flex items-center justify-center text-[#1f4d2b] transition">
          <FiChevronRight size={16} />
        </button>
      </div>
      <div className="p-3">
        <div className="grid grid-cols-7 mb-1">
          {dayNames.map(d => (
            <div key={d} className="text-center text-[9px] font-extrabold tracking-widest uppercase text-[#1f4d2b]/35 py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }).map((_,i) => <div key={`e${i}`} />)}
          {Array.from({ length: daysInMonth }, (_,i) => i+1).map(day => {
            const date    = new Date(year, month, day);
            const isPast  = date < today;
            const isToday = date.getTime() === today.getTime();
            const isSel   = selected && selected.getTime() === date.getTime();
            return (
              <button
                key={day}
                disabled={isPast}
                onClick={() => !isPast && onSelect(date)}
                className={[
                  'aspect-square flex items-center justify-center rounded-xl text-sm font-semibold border-[1.5px] transition-all',
                  isSel   ? 'bg-[#1f4d2b] text-white border-[#1f4d2b] shadow-md'
                  : isToday ? 'border-[#3d8c4a]/60 text-[#2d6b38] font-extrabold bg-transparent'
                  : isPast  ? 'text-[#1a3d20]/20 border-transparent cursor-not-allowed'
                  :           'text-[#1a3d20] border-transparent hover:bg-[#e6f4d8] hover:border-[#1f4d2b]/20 cursor-pointer',
                ].join(' ')}
              >{day}</button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── FIELD ──────────────────────────────────────────────── */
const inputCls = 'w-full bg-white border-[1.5px] border-[#dfd1bc] rounded-2xl px-4 py-3 font-semibold text-[#1a3d20] placeholder:text-[#1a3d20]/30 focus:outline-none focus:border-[#1f4d2b] focus:ring-2 focus:ring-[#1f4d2b]/10 transition text-sm';

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-[#1f4d2b]/50">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

/* ── BOOKING MODAL ──────────────────────────────────────── */
function BookingModal({ isJa, selected, onClose, formatDate }) {
  const [status,    setStatus]    = useState('idle');
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    name:'', email:'', phone:'', activity:'', time:'', groupSize: 2, message:''
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name || !form.email || !form.phone || !form.activity || !form.time) {
      setFormError(isJa ? '必須項目をすべて入力してください' : 'Please fill in all required fields');
      return;
    }
    setStatus('submitting');
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        guest_name:  form.name,
        guest_email: form.email,
        guest_phone: form.phone,
        visit_date:  formatDate(selected),
        visit_time:  form.time,
        activity:    form.activity,
        group_size:  `${form.groupSize}${isJa ? '名' : ' people'}`,
        message:     form.message || (isJa ? 'なし' : 'None'),
      }, EMAILJS_PUBLIC_KEY);
      setStatus('success');
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* modal */}
      <motion.div
        className="relative z-10 w-full sm:max-w-lg bg-[#faf7f0] rounded-t-[28px] sm:rounded-[28px] overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: '92vh' }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        onClick={e => e.stopPropagation()}
      >
        {/* green header bar */}
        <div className="bg-[#1f4d2b] px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xl">📅</span>
            <div>
              <p className="text-[9px] font-extrabold tracking-[.25em] uppercase text-white/50">
                {isJa ? '選択日' : 'Selected Date'}
              </p>
              <p className="text-white font-bold text-base">{formatDate(selected)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white/70 transition"
          >
            <FiX size={14} />
          </button>
        </div>

        {/* scrollable body */}
        <div className="overflow-y-auto flex-1 p-6">

          {/* idle — show form */}
          {status === 'idle' && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <p className="text-[10px] font-extrabold tracking-[.22em] uppercase text-[#1f4d2b]/45">
                {isJa ? '予約情報を入力してください' : 'Fill in your booking details'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={isJa ? 'お名前' : 'Full Name'} required>
                  <div className="relative">
                    <FiUser size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1a3d20]/35 pointer-events-none" />
                    <input className={inputCls + ' pl-10'} placeholder={isJa ? '山田 太郎' : 'Taro Yamada'}
                      value={form.name} onChange={e => set('name', e.target.value)} />
                  </div>
                </Field>
                <Field label={isJa ? '電話番号' : 'Phone'} required>
                  <div className="relative">
                    <FiSmartphone size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1a3d20]/35 pointer-events-none" />
                    <input type="tel" className={inputCls + ' pl-10'} placeholder="090-1234-5678"
                      value={form.phone} onChange={e => set('phone', e.target.value)} />
                  </div>
                </Field>
              </div>

              <Field label={isJa ? 'メールアドレス' : 'Email'} required>
                <div className="relative">
                  <FiMail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1a3d20]/35 pointer-events-none" />
                  <input type="email" className={inputCls + ' pl-10'} placeholder="example@email.com"
                    value={form.email} onChange={e => set('email', e.target.value)} />
                </div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={isJa ? '体験内容' : 'Activity'} required>
                  <select className={inputCls} value={form.activity} onChange={e => set('activity', e.target.value)}>
                    <option value="">{isJa ? '-- 選択 --' : '-- Select --'}</option>
                    {(isJa ? ACTIVITIES_JA : ACTIVITIES_EN).map((a, i) => (
                      <option key={a} value={a}>{ACT_ICONS[i]} {a}</option>
                    ))}
                  </select>
                </Field>
                <Field label={isJa ? '希望時間帯' : 'Time'} required>
                  <select className={inputCls} value={form.time} onChange={e => set('time', e.target.value)}>
                    <option value="">{isJa ? '-- 選択 --' : '-- Select --'}</option>
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>

              {/* group size stepper */}
              <Field label={isJa ? '人数' : 'Group Size'} required>
                <div className="flex items-center gap-3">
                  <button type="button"
                    onClick={() => set('groupSize', Math.max(1, form.groupSize - 1))}
                    className="w-12 h-12 rounded-2xl bg-[#f4f9ee] border-[1.5px] border-[#dfd1bc] text-[#1f4d2b] text-2xl font-black flex items-center justify-center hover:bg-[#ddf0d0] transition shrink-0">
                    −
                  </button>
                  <div className="flex-1 bg-[#f4f9ee] border-[1.5px] border-[#dfd1bc] rounded-2xl py-3 text-center">
                    <span className="text-3xl font-bold text-[#1a3d20]">{form.groupSize}</span>
                    <span className="text-sm text-[#1a3d20]/45 ml-2">{isJa ? '名' : 'people'}</span>
                  </div>
                  <button type="button"
                    onClick={() => set('groupSize', Math.min(20, form.groupSize + 1))}
                    className="w-12 h-12 rounded-2xl bg-[#f4f9ee] border-[1.5px] border-[#dfd1bc] text-[#1f4d2b] text-2xl font-black flex items-center justify-center hover:bg-[#ddf0d0] transition shrink-0">
                    ＋
                  </button>
                </div>
              </Field>

              <Field label={isJa ? 'その他・備考（任意）' : 'Notes (optional)'}>
                <textarea rows={3} className={inputCls + ' resize-none'}
                  placeholder={isJa ? '質問やご要望があればご記入ください' : 'Any questions or special requests?'}
                  value={form.message} onChange={e => set('message', e.target.value)} />
              </Field>

              {formError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-red-700 text-sm font-semibold">
                  <FiAlertCircle size={16} /> {formError}
                </div>
              )}

              <button type="submit"
                className="w-full bg-[#1f4d2b] hover:bg-[#2d6b38] text-white rounded-2xl py-5 text-lg font-black flex items-center justify-center gap-3 transition shadow-lg shadow-[#1f4d2b]/20">
                <FiSend size={18} />
                {isJa ? '予約を申し込む' : 'Submit Reservation'}
              </button>

              <p className="text-center text-xs text-[#1a3d20]/38 leading-relaxed">
                {isJa
                  ? '※ 送信後、営業時間内にお電話にてご確認します。お支払いは当日現地で。'
                  : '* We will call to confirm during business hours. Payment collected on the day.'}
              </p>
            </form>
          )}

          {/* submitting */}
          {status === 'submitting' && (
            <div className="py-16 text-center">
              <div className="w-14 h-14 border-4 border-[#1f4d2b]/20 border-t-[#1f4d2b] rounded-full animate-spin mx-auto mb-5" />
              <p className="text-[#1f4d2b] text-xl font-black">{isJa ? '送信中...' : 'Sending...'}</p>
            </div>
          )}

          {/* success */}
          {status === 'success' && (
            <div className="py-8 text-center">
              <div className="w-20 h-20 rounded-full bg-[#1f4d2b] flex items-center justify-center mx-auto mb-5 shadow-xl shadow-[#1f4d2b]/20">
                <FiCheckCircle size={40} color="#fff" />
              </div>
              <h3 className="text-[#173617] text-2xl font-black mb-3">
                {isJa ? 'ご予約を受け付けました！🎉' : 'Reservation Received! 🎉'}
              </h3>
              <p className="text-[#2d6b38] text-sm font-semibold leading-relaxed mb-2">
                {isJa
                  ? '確認メールをお送りしました。営業時間内にお電話にてご確認させていただきます。'
                  : 'A confirmation email has been sent. We will call to confirm during business hours.'}
              </p>
              <p className="text-[#2d6b38]/55 text-xs leading-relaxed mb-6">
                {isJa
                  ? '※ お支払いは当日・現地にて。予約確定はお電話確認後となります。'
                  : '* Payment on the day. Booking confirmed after our call.'}
              </p>
              <button onClick={onClose}
                className="bg-[#1f4d2b] hover:bg-[#2d6b38] text-white rounded-full px-8 py-4 text-base font-black transition">
                {isJa ? '閉じる' : 'Close'}
              </button>
            </div>
          )}

          {/* error */}
          {status === 'error' && (
            <div className="py-8 text-center">
              <div className="w-20 h-20 rounded-full bg-red-500 flex items-center justify-center mx-auto mb-5">
                <FiAlertCircle size={40} color="#fff" />
              </div>
              <h3 className="text-red-800 text-xl font-black mb-3">
                {isJa ? '送信に失敗しました' : 'Submission Failed'}
              </h3>
              <p className="text-red-600 text-sm font-semibold mb-6">
                {isJa ? 'お電話にてご予約ください。' : 'Please call us directly to reserve.'}
              </p>
              <a href={TEL}
                className="inline-flex items-center justify-center gap-3 bg-[#1f4d2b] hover:bg-[#2d6b38] text-white rounded-full px-8 py-4 text-lg font-black no-underline mb-3">
                <FiPhone size={18} /> {PHONE}
              </a>
            </div>
          )}

        </div>
      </motion.div>
    </div>
  );
}

/* ── MAIN ───────────────────────────────────────────────── */
export default function Reservation() {
  const { i18n } = useTranslation();
  const isJa = i18n.language === 'ja';

  const [selected,    setSelected]    = useState(null);
  const [modalOpen,   setModalOpen]   = useState(false);

  const formatDate = (date) => {
    if (!date) return '';
    if (isJa) return `${date.getFullYear()}年${date.getMonth()+1}月${date.getDate()}日`;
    return date.toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' });
  };

  const handleDateSelect = (date) => {
    setSelected(date);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelected(null);
  };

  return (
    <section id="reservation" className="relative overflow-hidden py-24 bg-[#f8f5ef]">

      {/* dot pattern */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage:'radial-gradient(circle,rgba(30,80,30,0.055) 1px,transparent 1px)', backgroundSize:'28px 28px', opacity:.6 }} />
      <div className="absolute -top-32 -right-20 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background:'radial-gradient(circle,rgba(45,107,56,0.08) 0%,transparent 70%)' }} />
      <div className="absolute -bottom-16 -left-16 w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{ background:'radial-gradient(circle,rgba(200,134,58,0.08) 0%,transparent 70%)' }} />
      <div className="absolute top-10 left-10 text-6xl opacity-[0.07] pointer-events-none select-none">🌿</div>
      <div className="absolute top-20 right-10 text-6xl opacity-[0.07] pointer-events-none select-none">🍃</div>

      <div className="max-w-7xl mx-auto px-5 relative z-10">

        {/* HEADER */}
        <motion.div initial={{ opacity:0, y:40 }} whileInView={{ opacity:1, y:0 }}
          transition={{ duration:.7 }} viewport={{ once:true }}
          className="text-center mb-16">
          <div className="inline-flex items-center gap-3 bg-[#1f4d2b] text-white rounded-full px-6 py-3 text-[10px] font-extrabold tracking-[0.22em] uppercase mb-6 shadow-xl shadow-[#1f4d2b]/20">
            <span className="w-2 h-2 rounded-full bg-[#7dc45a] animate-pulse" />
            {isJa ? 'オンライン予約受付中' : 'Online Reservations Open'}
          </div>
          <h2 className="font-bold text-[#173617] leading-[.92] tracking-tight mb-4"
            style={{ fontSize:'clamp(48px,7vw,80px)' }}>
            {isJa ? 'ご予約・' : 'Reservation & '}
            <em style={{ color:'#3d8c4a' }}>{isJa ? 'お問い合わせ' : 'Contact'}</em>
          </h2>
          <p className="text-[#6b4f2b] text-xl font-bold mb-5 max-w-lg mx-auto leading-relaxed">
            {isJa ? '日程を選んでフォームに入力するだけ' : 'Pick a date — the booking form opens instantly'}
          </p>
          <div className="inline-flex items-center gap-2 bg-white border-[1.5px] border-[#dfd1bc] rounded-full px-6 py-3 text-[#1f4d2b] text-sm font-bold shadow-sm">
            💴 {isJa ? 'お支払いは当日・現地にて。オンライン決済なし。' : 'Pay on the day at venue. No online payment.'}
          </div>
        </motion.div>

        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-[1.3fr_0.9fr] gap-6 mb-6">

          {/* LEFT — calendar */}
          <motion.div initial={{ opacity:0, x:-40 }} whileInView={{ opacity:1, x:0 }}
            transition={{ duration:.7 }} viewport={{ once:true }}
            className="bg-[#faf7f0] rounded-[32px] border-[1.5px] border-[#dfd1bc] shadow-xl overflow-hidden">
            <div className="bg-[#1f4d2b] px-8 py-5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center text-base shrink-0">📅</div>
              <span className="font-bold text-white text-sm tracking-wide">
                {isJa ? '日付を選ぶと予約フォームが開きます' : 'Tap a date to open the booking form'}
              </span>
            </div>
            <div className="p-6">
              <MiniCalendar isJa={isJa} selected={selected} onSelect={handleDateSelect} />

              {/* hint */}
              <div className="mt-5 bg-[#f8f3e7] border-[1.5px] border-[#e4d7bc] rounded-2xl p-4 text-center text-[#2d5a27] font-bold text-sm">
                ✨ {isJa ? '日付をタップすると予約フォームがポップアップします' : 'Tap any date to open the reservation popup'}
              </div>

              
            </div>
          </motion.div>

          {/* RIGHT */}
          <motion.div initial={{ opacity:0, x:40 }} whileInView={{ opacity:1, x:0 }}
            transition={{ duration:.7, delay:.1 }} viewport={{ once:true }}
            className="flex flex-col gap-5">

            {/* PHONE CARD */}
            <div className="bg-[#1f4d2b] rounded-[28px] p-8 text-center shadow-xl shadow-[#1f4d2b]/25 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/5 pointer-events-none" />
              <div className="text-6xl mb-4">📞</div>
              <p className="text-white/55 text-xs font-extrabold tracking-[.28em] uppercase mb-3">
                {isJa ? 'またはお電話で' : 'Or reserve by phone'}
              </p>
              <a href={TEL} className="text-white text-[32px] font-bold tracking-wider block mb-5 hover:opacity-80 transition no-underline">
                {PHONE}
              </a>
              <a href={TEL} className="flex items-center justify-center gap-3 bg-white text-[#1f4d2b] rounded-2xl py-4 text-sm font-extrabold tracking-wide uppercase hover:bg-[#e8f8d0] transition no-underline">
                <FiPhone size={16} /> {isJa ? '電話して予約する' : 'Call to Reserve'}
              </a>
            </div>

            {/* HOURS */}
            <div className="bg-[#faf7f0] rounded-[28px] border-[1.5px] border-[#dfd1bc] shadow-lg overflow-hidden">
              <div className="bg-[#2d5a27] text-white text-center py-4 text-xl font-black tracking-wide">
                {isJa ? '🕘 営業時間' : '🕘 Business Hours'}
              </div>
              <div className="p-5 flex flex-col gap-4">
                {[
                  { icon:'🌞', label: isJa?'通常営業':'Regular Hours', time:'9:30〜17:00', sub: isJa?'最終受付 16:30':'Last Entry 4:30 PM' },
                  { icon:'❄️', label: isJa?'冬季（11〜2月）':'Winter (Nov–Feb)', time:'〜16:30', sub: isJa?'最終受付 16:00':'Last Entry 4:00 PM' },
                ].map(row => (
                  <div key={row.label} className="bg-[#fffdf8] border border-[#e5d7bc] rounded-[20px] p-5 flex items-center gap-4">
                    <div className="text-4xl shrink-0">{row.icon}</div>
                    <div>
                      <p className="text-[#173617] text-lg font-black">{row.label}</p>
                      <p className="text-[#173617] text-3xl font-black leading-tight">{row.time}</p>
                      <p className="text-[#555] text-sm font-semibold mt-1">{row.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FEATURES */}
         {/* FEATURES — single row chips */}
<div className="flex flex-wrap gap-3">
  {[
    ['🅿️', isJa?'無料駐車場':'Free Parking'],
    ['🌍', isJa?'英語対応':'English OK'],
    ['👨‍👩‍👧', isJa?'ファミリー歓迎':'Families Welcome'],
    ['💴', isJa?'当日現地払い':'Pay on the Day'],
  ].map(([icon, text]) => (
    <div key={text} className="
      flex items-center gap-2
      bg-white border-[1.5px] border-[#dfd1bc]
      rounded-full px-4 py-2.5
      shadow-[0_3px_0_#dfd1bc]
    ">
      <span className="text-lg">{icon}</span>
      <span className="text-[#173617] text-sm font-extrabold whitespace-nowrap">{text}</span>
    </div>
  ))}
</div>
          </motion.div>
        </div>

      

      </div>

      {/* BOOKING MODAL */}
      <AnimatePresence>
        {modalOpen && (
          <BookingModal
            isJa={isJa}
            selected={selected}
            onClose={handleClose}
            formatDate={formatDate}
          />
        )}
      </AnimatePresence>

    </section>
  );
}